import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { rooms } from "../constants/rooms";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

const ReassignModal = ({ workOrder, onClose, onAssign }) => {
  const [step, setStep] = useState("ask");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [loading, setLoading] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const hasAllergens = workOrder.allergen && workOrder.allergen !== "None";

  const blockedRooms = useMemo(
    () => (hasAllergens ? ["Room 1", "Room 2"] : []),
    [hasAllergens],
  );

  const availableRooms = rooms.filter((r) => !blockedRooms.includes(r));

  useEffect(() => {
    if (!workOrder?.room) return;
    if (blockedRooms.includes(workOrder.room)) {
      setSelectedRoom("");
      toast("Current room is blocked due to allergens. Please choose another.");
    } else {
      setSelectedRoom(workOrder.room);
    }
  }, [workOrder, blockedRooms]);

  const handleSubmit = async () => {
    const roomToAssign = step === "confirm" ? workOrder.room : selectedRoom;

    if (!roomToAssign) {
      toast.error("Please select a room.");
      return;
    }

    if (roomToAssign === workOrder.room && step !== "confirm") {
      toast("Selected the same room.", { duration: 4000 });
      onClose();
      return;
    }

    setLoading(true);
    try {
      await onAssign(roomToAssign, today);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="reassign-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="reassign-modal-title" className="text-lg font-semibold mb-4">
          Re-assign {workOrder.woNumber}
        </h3>

        {hasAllergens && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
            This WO contains allergens. Room 1 and Room 2 are not available.
          </div>
        )}

        {step === "ask" && (
          <div className="space-y-4">
            <p className="text-sm">
              Do you want to assign to the same room ({workOrder.room})?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStep("confirm")}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={loading}
              >
                Yes
              </button>
              <button
                onClick={() => setStep("select")}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={loading}
              >
                No
              </button>
            </div>
          </div>
        )}

        {step === "select" && (
          <div className="space-y-4">
            <label
              htmlFor="room-select"
              className="block text-sm font-medium text-gray-700"
            >
              Select new room
            </label>
            <select
              id="room-select"
              value={selectedRoom}
              onChange={(e) => setSelectedRoom(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-black focus:outline-none"
              disabled={loading}
            >
              <option value="">-- Select Room --</option>
              {availableRooms.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStep("ask")}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={loading}
              >
                Back
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedRoom}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
              >
                {loading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                {loading ? "Processing..." : "Continue"}
              </button>
            </div>
          </div>
        )}

        {step === "confirm" && (
          <div className="space-y-4">
            <p className="text-sm">
              Will re-assign to <strong>{workOrder.room}</strong> on{" "}
              <strong>{today}</strong>.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStep("ask")}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium"
                disabled={loading}
              >
                Back
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded text-sm font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
              >
                {loading && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReassignModal;
